import { useEffect, useMemo } from 'react';
import { SWRConfiguration } from 'swr';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import {
  useExampleRunsInfinite,
  useGroupByExampleRunsInfinite,
} from '@/hooks/useSwr';
import {
  ExampleSchemaWithRunsAndOptionalFields,
  ExamplesGroup,
} from '@/types/schema';

import { useGroupByMetadata } from './useGroupByMetadata';
import { useGroupCollapse } from './useGroupCollapse';
import { getKeyName, isExampleMetadata } from './utils';

interface GroupPaginationState {
  currentPage: number;
  totalExamples: number;
  hasMore: boolean;
}

interface GroupsState {
  groups: ExamplesGroup[];
  paginationState: Record<string, GroupPaginationState>;
  boundSWRMutates: Record<string, () => void>;
  appendGroups: (newGroups: ExamplesGroup[]) => void;
  updatePaginationState: (
    groupKey: string,
    update: Partial<GroupPaginationState>
  ) => void;
  appendExamplesToGroup: (
    groupKey: string,
    examples: ExampleSchemaWithRunsAndOptionalFields[]
  ) => void;
  reset: () => void;
  // used for getting the correct example mutate function
  bindSWRMutate: (groupKey: string, mutate: () => void) => void;
}

const useGroupsStore = create<GroupsState>()(
  immer((set) => ({
    groups: [] as ExamplesGroup[],
    paginationState: {} as Record<string, GroupPaginationState>,
    boundSWRMutates: {} as Record<string, () => void>,
    appendGroups: (newGroups) =>
      set((state) => {
        const brandNewGrops = newGroups.filter(
          (g) => !state.groups.some((g2) => g2.group_key === g.group_key)
        );
        state.groups = state.groups.map((g) => {
          const updatedGroup = newGroups.find(
            (g2) => g2.group_key === g.group_key
          );
          if (updatedGroup) {
            return updatedGroup;
          }
          return g;
        });
        state.groups.push(...brandNewGrops);
        brandNewGrops.forEach((group) => {
          // initialize pagination state for new groups
          if (!state.paginationState[group.group_key]) {
            state.paginationState[group.group_key] = {
              currentPage: 1,
              totalExamples: group.examples.length,
              hasMore: group.count > group.examples.length,
            };
          }
        });
      }),
    updatePaginationState: (groupKey, update) =>
      set((state) => {
        const current = state.paginationState[groupKey] ?? {
          currentPage: 1,
          totalExamples: 0,
          hasMore: true,
        };
        state.paginationState[groupKey] = { ...current, ...update };
      }),
    appendExamplesToGroup: (groupKey, examples) =>
      set((state) => {
        const groupIndex = state.groups.findIndex(
          (g) => g.group_key === groupKey
        );
        if (groupIndex >= 0) {
          const existingIds = new Set(
            state.groups[groupIndex].examples.map((e) => e.id)
          );
          const uniqueExamples = examples.filter((e) => !existingIds.has(e.id));
          state.groups[groupIndex].examples.push(...uniqueExamples);
        }
      }),
    reset: () =>
      set((state) => {
        state.groups = [];
        state.paginationState = {};
        state.boundSWRMutates = {};
      }),
    bindSWRMutate: (groupKey, mutate) =>
      set((state) => {
        state.boundSWRMutates[groupKey] = mutate;
      }),
  }))
);

interface UseGroupByExamplesResult {
  groups: ExamplesGroup[] | undefined;
  flatExamples:
    | (ExampleSchemaWithRunsAndOptionalFields & {
        isFirstExampleInGroup: boolean;
        isLastExampleInGroup: boolean;
        isGroupCollapsed: boolean;
        groupIndex: number;
      })[]
    | undefined;
  hasMoreToLoad: boolean;
  isNextPageValidating: boolean;
  areGroupsLoading: boolean;
  setGroupsSize: (size: number | ((size: number) => number)) => void;
  groupsError?: Error;
  boundSWRMutates: Record<string, () => void>;
}

export function useGroupByExamples(
  datasetId: string | undefined,
  sessionIds: string[],
  datasetShareToken?: string
): UseGroupByExamplesResult {
  const { metadataKey: groupByMetadataKey } = useGroupByMetadata();
  const collapsedStates = useGroupCollapse((state) => state.collapsedStates);

  const groupByType = isExampleMetadata(groupByMetadataKey)
    ? 'example_metadata'
    : 'run_metadata';
  const formattedKeyName = getKeyName(groupByMetadataKey);

  const storedGroups = useGroupsStore((state) => state.groups);
  const boundSWRMutates = useGroupsStore((state) => state.boundSWRMutates);

  const appendGroups = useGroupsStore((state) => state.appendGroups);
  const reset = useGroupsStore((state) => state.reset);

  const {
    data: groupsPages,
    isLoading: areGroupsLoading,
    isValidating: areGroupsValidating,
    size: groupsSize,
    setSize: setGroupsSize,
    error: groupsError,
  } = useGroupByExampleRunsInfinite(
    10,
    datasetId,
    sessionIds?.length && formattedKeyName
      ? {
          session_ids: sessionIds,
          group_by: groupByType,
          metadata_key: formattedKeyName,
        }
      : null,
    {
      datasetShareToken,
    }
  );

  useEffect(() => {
    reset();
  }, [datasetId, formattedKeyName, reset]);

  // Append new groups when pages update
  useEffect(() => {
    if (!groupsPages) return;
    const newGroups = groupsPages[groupsPages.length - 1]?.groups ?? [];
    if (newGroups.length > 0) {
      appendGroups(newGroups);
    }
  }, [groupsPages, appendGroups]);

  const flatExamples = useMemo(
    () =>
      storedGroups.flatMap((g, groupIndex) =>
        g.examples.map((e, index) => ({
          ...e,
          isGroupCollapsed:
            collapsedStates[`${groupByMetadataKey?.value}:${g.group_key}`],
          isFirstExampleInGroup: index === 0,
          isLastExampleInGroup: index === g.examples.length - 1,
          groupIndex,
        }))
      ),
    [storedGroups, collapsedStates, groupByMetadataKey]
  );

  const total =
    (groupsPages ?? [])
      .slice()
      .reverse()
      .find((page) => page.groups.length > 0)?.headers?.[
      'x-pagination-total'
    ] ?? 0;
  const hasMoreToLoad = Boolean(storedGroups.length < total);
  const isNextPageValidating =
    areGroupsValidating && groupsSize > 1 && groupsPages?.length != groupsSize;

  return {
    groups: storedGroups,
    flatExamples,
    hasMoreToLoad,
    isNextPageValidating,
    areGroupsLoading,
    setGroupsSize,
    groupsError,
    boundSWRMutates,
  };
}

export function useSingleGroup({
  datasetId,
  sessionId,
  datasetShareToken,
  group,
  swrOptions,
}: {
  datasetId?: string;
  sessionId?: string;
  group: ExamplesGroup;

  datasetShareToken?: string;
  swrOptions?: SWRConfiguration;
}) {
  const updatePaginationState = useGroupsStore(
    (state) => state.updatePaginationState
  );
  const appendExamplesToGroup = useGroupsStore(
    (state) => state.appendExamplesToGroup
  );
  const paginationState = useGroupsStore(
    (state) => state.paginationState[group.group_key]
  );
  const bindSWRMutate = useGroupsStore((state) => state.bindSWRMutate);
  const filterPerSession = {
    [sessionId ?? '']: [group.filter],
  };
  const keyValue = group.group_key;

  const {
    data: examplePages,
    size: examplesSize,
    setSize: setExamplesSize,
    isLoading: isExamplesLoading,
    isValidating: isNextPageValidating,
    error: examplesError,
    mutate: mutateExamples,
  } = useExampleRunsInfinite(
    5,
    datasetId ?? null,
    sessionId && paginationState?.hasMore
      ? {
          session_ids: [sessionId],
          filters: filterPerSession,
          preview: true,
        }
      : null,
    { datasetShareToken },
    {
      revalidateFirstPage: false,
      initialSize: 1,
      revalidateOnFocus: false,
      ...swrOptions,
    }
  );

  // Bind the mutate function to the group key
  useEffect(() => {
    bindSWRMutate(keyValue, mutateExamples);
  }, [mutateExamples, keyValue, bindSWRMutate]);

  // Existing effect for handling updates
  useEffect(() => {
    if (!examplePages?.length) return;

    const lastPage = examplePages[examplePages.length - 1];
    const total = lastPage.headers?.['x-pagination-total'] ?? 0;
    const loadedCount = examplePages.reduce(
      (sum, page) => sum + page.length,
      0
    );
    const hasMore = loadedCount < total;

    updatePaginationState(keyValue, {
      currentPage: examplesSize,
      totalExamples: total,
      hasMore,
    });

    // Sync all cached examples to the group
    examplePages.forEach((page) => {
      if (page?.length > 0) {
        appendExamplesToGroup(keyValue, page);
      }
    });
  }, [
    examplePages,
    examplesSize,
    keyValue,
    updatePaginationState,
    appendExamplesToGroup,
  ]);

  return {
    hasMoreToLoad: paginationState?.hasMore,
    currentPage: paginationState?.currentPage,
    setExamplesSize,
    isExamplesLoading,
    isNextPageValidating,
    examplesError,
  };
}
